import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
} from 'react';
import Cookies from 'js-cookie';
import { User, authService } from '../services/auth';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'INIT_COMPLETE' }; // Nueva acción para completar la inicialización

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true, // Start loading to verify cookies
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'INIT_COMPLETE':
      return {
        ...state,
        isLoading: false,
      };
    default:
      return state;
  }
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  register: (data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  setUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const token = Cookies.get('access_token');
    const storedUser = Cookies.get('user_data');

    if (token && storedUser) {
      try {
        const user = JSON.parse(storedUser);
        dispatch({ type: 'AUTH_SUCCESS', payload: user });
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        Cookies.remove('access_token');
        Cookies.remove('user_data');
        dispatch({ type: 'INIT_COMPLETE' });
      }
    } else {
      // No hay tokens, completar la inicialización
      dispatch({ type: 'INIT_COMPLETE' });
    }
  }, []);

  const login = async (credentials: {
    email: string;
    password: string;
  }): Promise<void> => {
    dispatch({ type: 'AUTH_START' });
    try {
      const response = await authService.login(credentials);

      // Guardar en cookies con expiración de 7 días
      Cookies.set('access_token', response.access_token, {
        expires: 7,
        secure: import.meta.env.PROD,
        sameSite: 'lax',
      });
      Cookies.set('user_data', JSON.stringify(response.user), {
        expires: 7,
        secure: import.meta.env.PROD,
        sameSite: 'lax',
      });

      dispatch({ type: 'AUTH_SUCCESS', payload: response.user });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Login failed';
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  };

  const register = async (data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }): Promise<void> => {
    dispatch({ type: 'AUTH_START' });
    try {
      await authService.register(data);
      // Note: After registration, user might need approval
      dispatch({ type: 'LOGOUT' });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Registration failed';
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  };

  const logout = (): void => {
    authService.logout();
    Cookies.remove('access_token');
    Cookies.remove('user_data');
    dispatch({ type: 'LOGOUT' });
  };

  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const setUser = (user: User): void => {
    dispatch({ type: 'AUTH_SUCCESS', payload: user });
    Cookies.set('user_data', JSON.stringify(user), {
      expires: 7,
      secure: import.meta.env.PROD,
      sameSite: 'lax',
    });
  };

  const contextValue: AuthContextType = {
    user: state.user,
    loading: state.isLoading,
    error: state.error,
    isAuthenticated: state.isAuthenticated,
    login,
    register,
    logout,
    clearError,
    setUser,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
